<script lang="ts" setup>
import { ref, computed } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useColorMode } from "@vueuse/core";

const router = useRouter();
const route = useRoute();
// 恢复useColorMode，但不强制设置主题
const mode = useColorMode();

// 检测是否应该显示面包屑（与面包屑组件的逻辑保持一致）
const showBreadcrumb = computed(() => {
  return route.name !== undefined
});

// 当前激活的导航项
const activeRoute = ref<string>('');

// 检测当前激活的产品页面
const activeProductPath = computed(() => {
  const path = route.path;
  if (path.startsWith('/products/') || path.startsWith('/chuangchengai') || path.startsWith('/blog')) {
    return path;
  }
  return null;
});

// 判断是否在产品/博客页面
const isInProductPage = computed(() => {
  return activeProductPath.value !== null;
});

// 动态导航列表 - 根据当前页面显示不同内容
const dynamicRouteList = computed(() => {
  if (isInProductPage.value) {
    // 在产品页面时，显示返回首页和其他产品
    const otherProducts = featureList.filter(item => item.href !== activeProductPath.value);
    return [
      { href: '/', label: '返回首页' },
      ...otherProducts.slice(0, 4).map(item => ({ href: item.href || '#', label: item.title }))
    ];
  } else {
    // 在首页时，显示锚点链接
    return routeList;
  }
});

// 检测当前页面位置并更新激活状态
const updateActiveRoute = () => {
  const sections = ['benefits', 'services', 'testimonials', 'blog-preview', 'pricing', 'contact'];
  const scrollPosition = window.scrollY + 100;

  for (const section of sections) {
    const element = document.getElementById(section);
    if (element) {
      const offsetTop = element.offsetTop;
      const offsetHeight = element.offsetHeight;

      if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
        activeRoute.value = `#${section}`;
        break;
      }
    }
  }
};

// 监听滚动事件
if (typeof window !== 'undefined') {
  window.addEventListener('scroll', updateActiveRoute);
}

import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

import { Menu } from "lucide-vue-next";
import ToggleTheme from "./ToggleTheme.vue";

interface RouteProps {
  href: string;
  label: string;
}

interface FeatureProps {
  title: string;
  description: string;
  href?: string;
}

const routeList: RouteProps[] = [
  {
    href: "/#services",
    label: "产品服务",
  },

  {
    href: "/#benefits",
    label: "核心优势",
  },

  {
    href: "/#testimonials",
    label: "客户评价",
  },

  {
    href: "/#pricing",
    label: "价格方案",
  },
  {
    href: "/#contact",
    label: "联系我们",
  },
  {
    href: "/#blog-preview",
    label: "技术博客",
  },

];

const featureList: FeatureProps[] = [
  // {
  //   title: "创诚科技AI助手",
  //   description:
  //     "智能对话机器人，专业技术咨询，24小时在线服务，基于深度学习的智能问答系统。",
  //   href: "/chuangchengai"
  // },
  {
    title: "智能能耗管理",
    description: "全方位能源监测与优化解决方案，实时监控用电、用水、碳排放数据。",
    href: "/products/energy-management"
  },
  {
    title: "工业自动化控制",
    description:
      "先进的工业4.0自动化控制系统，PLC编程、SCADA系统、远程监控。",
    href: "/products/industrial-automation"
  },
  {
    title: "智能仪器仪表",
    description:
      "高精度智能检测设备，精密测量、数据采集、无线传输。",
    href: "/products/intelligent-instruments"
  },
  {
    title: "AI智能解决方案",
    description:
      "160 TOPS算力AI盒子，视频检测、人员识别、智能分析。",
    href: "/products/ai-solutions"
  },
  {
    title: "企业级软件定制",
    description:
      "ERP、MES、进销存、办公软件等企业级应用定制开发，数字化转型专家。",
    href: "/products/enterprise-software"
  },
];

const isOpen = ref<boolean>(false);

// 处理导航点击
const handleNavClick = (href: string, event?: Event) => {
  // 如果是锚点链接，阻止默认路由行为，使用平滑滚动
  if (href.startsWith('/#')) {
    event?.preventDefault();
    const targetId = href.substring(2); // 移除 "/#" 获取目标ID
    const targetElement = document.getElementById(targetId);

    if (targetElement) {
      targetElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  } else if (href.startsWith('/')) {
    // 如果是路由链接，使用router进行跳转
    event?.preventDefault();
    router.push(href);
  }

  activeRoute.value = href;
  isOpen.value = false; // 关闭移动端菜单
};
</script>

<template>
  <header role="banner" :class="[
    'navbar-header',
    showBreadcrumb ? 'navbar-with-breadcrumb' : 'navbar-normal',
    mode === 'light' ? 'shadow-light' : 'shadow-dark'
  ]">
    <a href="/" class="navbar-logo group" aria-label="创诚纪元人工智能首页">
      <img src="@/assets/logo.svg" alt="创诚纪元人工智能 - 物联网+AI智能解决方案专家" class="navbar-logo-img" width="36" height="36" />
      <span class="navbar-brand-text">
        创诚纪元人工智能
      </span>
    </a>
    <!-- 移动端导航 -->
    <nav class="mobile-nav" role="navigation" aria-label="移动端主导航">
      <Sheet v-model:open="isOpen">
        <SheetTrigger as-child>
          <button @click="isOpen = true" class="mobile-menu-button" aria-label="打开导航菜单" aria-expanded="false"
            aria-controls="mobile-navigation">
            <Menu class="h-5 w-5" />
          </button>
        </SheetTrigger>

        <SheetContent id="mobile-navigation" side="left"
          class="flex flex-col justify-between rounded-tr-2xl rounded-br-2xl bg-card max-h-screen overflow-hidden"
          role="dialog" aria-label="移动端导航菜单">
          <div class="flex-1 overflow-y-auto">
            <SheetHeader class="mb-4 ml-4">
              <SheetTitle class="flex items-center">
                <a href="/" class="flex items-center group transition-all duration-300" aria-label="创诚纪元人工智能首页">
                  <img src="@/assets/logo.svg" alt="创诚纪元人工智能logo"
                    class="size-9 mr-2 rounded-lg transition-all duration-300 group-hover:scale-110" width="36"
                    height="36" />
                  <span class="transition-colors duration-200 group-hover:text-primary">
                    创诚纪元人工智能
                  </span>
                </a>
              </SheetTitle>
            </SheetHeader>

            <!-- 移动端导航链接 -->
            <nav class="flex flex-col gap-3" role="navigation" aria-label="主要页面导航">
              <!-- 产品与技术分组 -->
              <div class="mobile-nav-section">
                <h4 class="mobile-nav-section-title">产品与技术</h4>
                <div class="flex flex-col gap-1 ml-3">
                  <Button v-for="{ title, href } in featureList" :key="title" as-child
                    :variant="activeProductPath === href ? 'default' : 'ghost'" :class="[
                      'mobile-nav-sub-item group',
                      activeProductPath === href ? 'bg-primary text-primary-foreground' : ''
                    ]">
                    <a @click="handleNavClick(href || '#', $event)" :href="href || '#'" class="nav-link"
                      :title="`前往${title}页面`" :aria-current="activeProductPath === href ? 'page' : undefined">
                      <span class="text-sm">{{ title }}</span>
                      <!-- 激活指示器 -->
                      <span v-if="activeProductPath === href" class="ml-auto">✓</span>
                      <!-- 悬停动画效果 -->
                      <div v-if="activeProductPath !== href" class="nav-hover-effect"></div>
                    </a>
                  </Button>
                </div>
              </div>

              <!-- 分隔线 -->
              <Separator class="my-1" />

              <!-- 主要导航链接 - 动态显示 -->
              <div class="flex flex-col gap-2">
                <Button v-for="{ href, label } in dynamicRouteList" :key="label" as-child
                  :variant="activeRoute === href || activeProductPath === href ? 'default' : 'ghost'" :class="[
                    'mobile-nav-item group',
                    activeRoute === href || activeProductPath === href ? 'mobile-nav-item-active' : 'mobile-nav-item-normal'
                  ]">
                  <a @click="handleNavClick(href, $event)" :href="href" class="nav-link"
                    :aria-current="activeRoute === href || activeProductPath === href ? 'page' : undefined"
                    :title="`前往${label}页面`">
                    <span v-if="activeRoute === href || activeProductPath === href" class="nav-indicator"></span>
                    {{ label }}
                    <!-- 悬停动画效果 -->
                    <div v-if="activeRoute !== href && activeProductPath !== href" class="nav-hover-effect"></div>
                  </a>
                </Button>
              </div>
            </nav>
          </div>

          <SheetFooter class="flex-col sm:flex-col justify-start items-start">
            <Separator class="mb-2" />

            <ToggleTheme />
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </nav>

    <!-- 桌面端导航菜单 -->
    <NavigationMenu class="desktop-nav" role="navigation" aria-label="桌面端主导航">
      <NavigationMenuList>
        <NavigationMenuItem>
          <NavigationMenuTrigger class="nav-trigger">
            产品与技术
          </NavigationMenuTrigger>
          <NavigationMenuContent>
            <div class="nav-content">
              <div class="company-showcase group" @click="router.push('/about')">
                <!-- 动态渐变背景层 -->
                <div class="absolute inset-0 animated-gradient"></div>

                <!-- 浮动装饰气泡 -->
                <div
                  class="absolute top-2 right-8 w-16 h-16 bg-gradient-to-br from-primary/25 to-primary/10 rounded-full blur-lg floating-bubble-1">
                </div>
                <div
                  class="absolute bottom-4 left-6 w-20 h-20 bg-gradient-to-tr from-primary/20 to-primary/5 rounded-full blur-xl floating-bubble-2">
                </div>
                <div
                  class="absolute top-1/2 right-2 w-12 h-12 bg-gradient-to-bl from-primary/30 to-transparent rounded-full blur-md floating-bubble-3">
                </div>

                <!-- 流动光效 -->
                <div class="absolute inset-0 flowing-light"></div>

                <!-- 闪烁装饰点 -->
                <div class="absolute top-4 right-4 w-2 h-2 bg-primary/60 rounded-full animate-pulse"></div>
                <div class="absolute bottom-6 left-4 w-1.5 h-1.5 bg-primary/40 rounded-full animate-pulse"
                  style="animation-delay: 1s;"></div>

                <div class="relative z-10 flex flex-col items-center">
                  <!-- Logo容器 -->
                  <div class="relative mb-4 group-hover:scale-110 transition-transform duration-500">
                    <div
                      class="absolute inset-0 bg-primary/20 rounded-2xl blur-md scale-110 group-hover:bg-primary/30 transition-colors duration-500">
                    </div>
                    <img src="@/assets/icon.ico" alt="创诚纪元人工智能 - 物联网+AI智能解决方案专家企业标识"
                      class="relative w-20 h-20 drop-shadow-lg" width="80" height="80" loading="lazy" />
                  </div>

                  <!-- 标题 -->
                  <h3
                    class="text-lg font-bold text-center mb-3 bg-gradient-to-r from-foreground to-primary bg-clip-text text-transparent group-hover:from-primary group-hover:to-primary/80 transition-all duration-500">
                    创诚纪元人工智能
                  </h3>

                  <!-- 描述文本 -->
                  <p
                    class="text-sm text-muted-foreground text-center leading-relaxed group-hover:text-foreground transition-colors duration-300">
                    专注于<span class="text-primary font-medium">物联网+AI</span>技术融合，为企业提供专业的智能化解决方案
                  </p>

                  <!-- 底部装饰线 -->
                  <div
                    class="mt-4 w-12 h-0.5 bg-gradient-to-r from-transparent via-primary to-transparent group-hover:w-20 transition-all duration-500">
                  </div>
                </div>
              </div>
              <ul class="flex flex-col gap-2">
                <li v-for="{ title, description, href } in featureList" :key="title" :class="[
                  'product-item group',
                  activeProductPath === href ? 'product-item-active' : ''
                ]" @click="href ? router.push(href) : null"
                  :aria-current="activeProductPath === href ? 'page' : undefined">
                  <p class="product-title">
                    {{ title }}
                    <!-- 激活指示器 -->
                    <span v-if="activeProductPath === href" class="ml-2 text-primary">✓</span>
                  </p>
                  <p class="product-description">{{ description }}</p>

                  <!-- 悬停时的左边界线 -->
                  <div class="product-border-line"></div>
                  <!-- 悬停时的渐变背景 -->
                  <div :class="[
                    'product-hover-bg',
                    activeProductPath === href ? 'opacity-20' : ''
                  ]"></div>
                  <!-- 激活状态的背景 -->
                  <div v-if="activeProductPath === href" class="absolute inset-0 bg-primary/10 rounded-lg"></div>
                </li>
              </ul>
            </div>
          </NavigationMenuContent>
        </NavigationMenuItem>

        <NavigationMenuItem>
          <NavigationMenuLink asChild>
            <div class="flex items-center gap-1">
              <Button v-for="{ href, label } in dynamicRouteList" :key="label" as-child
                :variant="activeRoute === href || activeProductPath === href ? 'default' : 'ghost'" :class="[
                  'text-base relative overflow-hidden group transition-all duration-300',
                  activeRoute === href || activeProductPath === href
                    ? 'bg-primary text-primary-foreground shadow-md scale-105'
                    : 'hover:bg-primary/10 hover:text-primary hover:scale-105'
                ]">
                <a @click="handleNavClick(href, $event)" :href="href"
                  class="relative z-10 px-4 py-2 rounded-md transition-all duration-200">
                  <!-- 激活指示器 -->
                  <span v-if="activeRoute === href || activeProductPath === href"
                    class="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-primary-foreground rounded-full animate-pulse"></span>

                  {{ label }}

                  <!-- 悬停渐变效果 -->
                  <div v-if="activeRoute !== href && activeProductPath !== href"
                    class="absolute inset-0 bg-gradient-to-r from-primary/10 via-primary/20 to-primary/10 opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-x-0 group-hover:scale-x-100 rounded-md">
                  </div>

                  <!-- 底部线条动画 -->
                  <div v-if="activeRoute !== href && activeProductPath !== href"
                    class="absolute bottom-0 left-0 w-full h-0.5 bg-primary transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-center">
                  </div>
                </a>
              </Button>
            </div>
          </NavigationMenuLink>
        </NavigationMenuItem>
      </NavigationMenuList>
    </NavigationMenu>

    <div class="hidden xl:flex items-center gap-2 flex-shrink-0">
      <!-- 主题切换按钮容器 -->
      <div class="relative flex-shrink-0">
        <ToggleTheme />
      </div>

      <!-- 分隔线 -->
      <div class="w-px h-6 bg-border/50 flex-shrink-0"></div>

      <!-- 立即咨询按钮 -->
      <Button size="sm" variant="default"
        :class="['cta-button group flex-shrink-0', activeRoute === '#contact' ? 'cta-button-active' : '']"
        @click="handleNavClick('/#contact', $event)">
        <div class="cta-content">
          <!-- 咨询图标 -->
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
            class="transition-transform duration-200 group-hover:scale-110">
            <path
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>

          <span class="whitespace-nowrap">立即咨询</span>

          <!-- 箭头图标 -->
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
            class="transition-transform duration-200 group-hover:translate-x-1">
            <path d="m9 18 6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round"
              stroke-linejoin="round" />
          </svg>

          <!-- 按钮发光效果 -->
          <div
            class="absolute inset-0 bg-gradient-to-r from-white/10 via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-md">
          </div>

          <!-- 扫光效果 -->
          <div
            class="absolute inset-0 -inset-x-4 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500 transform -skew-x-12 group-hover:translate-x-full">
          </div>
        </div>
      </Button>
    </div>
  </header>
</template>

<style scoped>
/* stylelint-disable at-rule-no-unknown */
/* 主要导航栏样式 */
.navbar-header {
  @apply w-[95%] sm:w-[90%] md:w-[70%] lg:w-[75%] xl:w-[85%] mx-auto sticky border z-40 rounded-2xl flex justify-between items-center p-2 bg-card shadow-md gap-2;
  max-width: 1400px;
}

.navbar-with-breadcrumb {
  @apply top-16;
}

.navbar-normal {
  @apply top-5;
}

.navbar-logo {
  @apply font-bold text-lg flex items-center transition-all duration-300 hover:scale-105 flex-shrink-0;
}

.navbar-logo-img {
  @apply w-9 h-9 mr-2 rounded-lg transition-all duration-300 group-hover:scale-110 group-hover:rotate-12 flex-shrink-0;
}

.navbar-brand-text {
  @apply transition-colors duration-200 group-hover:text-primary whitespace-nowrap;
}

/* 移动端导航样式 */
.mobile-nav {
  @apply flex items-center xl:hidden;
}

.mobile-menu-button {
  @apply cursor-pointer menu-button-rotate p-2 rounded-md hover:bg-accent transition-colors;
}

.mobile-nav-item {
  @apply justify-start text-base transition-all duration-200 relative overflow-hidden;
}

.mobile-nav-item-active {
  @apply bg-primary text-primary-foreground shadow-lg;
}

.mobile-nav-item-normal {
  @apply hover:bg-primary/10 hover:text-primary;
}

.nav-link {
  @apply relative z-10 flex items-center gap-2;
}

.nav-indicator {
  @apply w-2 h-2 bg-primary-foreground rounded-full animate-pulse;
}

.nav-hover-effect {
  @apply absolute inset-0 bg-gradient-to-r from-primary/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300;
}

/* 移动端导航分组样式 */
.mobile-nav-section {
  @apply space-y-2;
}

.mobile-nav-section-title {
  @apply text-sm font-semibold text-foreground/80 px-4 py-2 border-l-2 border-primary/30 bg-primary/5 rounded-r-md;
}

.mobile-nav-sub-item {
  @apply justify-start text-sm text-muted-foreground hover:text-foreground hover:bg-primary/5 transition-all duration-200 relative overflow-hidden;
}

/* 桌面端导航样式 */
.desktop-nav {
  @apply hidden xl:block flex-1;
  min-width: 0;
}

.nav-trigger {
  @apply bg-card text-base;
}

.nav-content {
  @apply grid w-[700px] grid-cols-2 gap-5 p-4;
}

.company-showcase {
  @apply relative flex flex-col justify-center items-center rounded-xl p-6 overflow-hidden hover:shadow-lg transition-all duration-500 hover:scale-[1.02] dynamic-gradient-bg cursor-pointer;
}

.product-item {
  @apply rounded-md p-3 text-sm cursor-pointer transition-all duration-300 relative overflow-hidden border border-transparent hover:bg-primary/10 hover:border-primary/20 hover:shadow-md hover:scale-[1.02];
}

.product-title {
  @apply mb-1 font-semibold leading-none text-foreground group-hover:text-primary transition-colors duration-200;
}

.product-description {
  @apply line-clamp-2 text-muted-foreground group-hover:text-foreground transition-colors duration-200;
}

.product-border-line {
  @apply absolute left-0 top-0 w-1 h-full bg-primary transform scale-y-0 group-hover:scale-y-100 transition-transform duration-300 origin-top;
}

.product-hover-bg {
  @apply absolute inset-0 bg-gradient-to-r from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-md;
}

/* 产品项激活状态 */
.product-item-active {
  @apply border-primary/30 bg-primary/5;
}

.product-item-active .product-title {
  @apply text-primary font-semibold;
}

.product-item-active .product-description {
  @apply text-foreground;
}

.product-item-active .product-border-line {
  @apply scale-y-100;
}

/* CTA按钮样式 */
.cta-button {
  @apply relative overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-lg bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary border border-primary/20 hover:border-primary/40 text-primary-foreground font-medium;
  padding: 0.375rem 0.75rem !important;
  height: auto !important;
  min-width: auto !important;
}

.cta-button-active {
  @apply ring-2 ring-primary/50 ring-offset-2 shadow-xl scale-105;
}

.cta-content {
  @apply relative z-10 flex items-center gap-1.5;
  font-size: 0.875rem;
}

/* 导航栏交互动画 */
@keyframes slideInFromTop {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes glowPulse {

  0%,
  100% {
    box-shadow: 0 0 5px hsl(var(--primary) / 0.5);
  }

  50% {
    box-shadow: 0 0 20px hsl(var(--primary) / 0.8), 0 0 30px hsl(var(--primary) / 0.4);
  }
}

@keyframes floatUpDown {

  0%,
  100% {
    transform: translateY(0px);
  }

  50% {
    transform: translateY(-2px);
  }
}

/* 导航项激活状态动画 */
.nav-item-active {
  animation: slideInFromTop 0.3s ease-out;
}

/* Logo悬停发光效果 */
.logo-glow:hover {
  animation: glowPulse 2s ease-in-out infinite;
}

/* 浮动动画 */
.float-animation {
  animation: floatUpDown 3s ease-in-out infinite;
}

/* 导航菜单渐入动画 */
.nav-menu-item {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.nav-menu-item:hover {
  transform: translateX(4px);
}

/* 移动端菜单按钮旋转动画 */
.menu-button-rotate {
  transition: transform 0.3s ease;
}

.menu-button-rotate:hover {
  transform: rotate(90deg);
}

/* CTA按钮渐变动画 */
.cta-button-gradient {
  background: linear-gradient(45deg, hsl(var(--primary)), hsl(var(--primary) / 0.8));
  position: relative;
  overflow: hidden;
}

.cta-button-gradient::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s;
}

.cta-button-gradient:hover::before {
  left: 100%;
}

/* 响应式动画增强 */
@media (max-width: 1024px) {
  .float-animation {
    animation-duration: 2s;
  }

  /* 移动端导航栏调整 */
  .navbar-header {
    @apply px-3 py-2;
  }

  .navbar-brand-text {
    @apply text-base;
  }
}

/* 超小屏幕适配 */
@media (max-width: 480px) {
  .navbar-header {
    @apply w-[98%] px-2 py-1.5;
  }

  .navbar-brand-text {
    @apply text-sm;
  }

  .navbar-logo-img {
    @apply w-8 h-8;
  }
}

/* 按钮区域样式增强 */
.navbar-actions {
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.dark .navbar-actions {
  background: rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.05);
}

/* 主题切换按钮增强 */
.theme-toggle-enhanced {
  position: relative;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 8px;
  padding: 8px;
}

.theme-toggle-enhanced:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: scale(1.05);
}

.dark .theme-toggle-enhanced:hover {
  background: rgba(255, 255, 255, 0.05);
}

/* 咨询按钮特殊效果 */
.consultation-button {
  position: relative;
  background: linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--primary) / 0.9) 100%);
  box-shadow:
    0 4px 14px hsl(var(--primary) / 0.25),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.consultation-button:hover {
  background: linear-gradient(135deg, hsl(var(--primary) / 0.9) 0%, hsl(var(--primary)) 100%);
  box-shadow:
    0 6px 20px hsl(var(--primary) / 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
  transform: translateY(-1px) scale(1.02);
}

.consultation-button:active {
  transform: translateY(0) scale(0.98);
  box-shadow:
    0 2px 8px hsl(var(--primary) / 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
}

/* 分隔线动画 */
.separator-line {
  transition: all 0.3s ease;
  background: linear-gradient(to bottom,
      transparent 0%,
      hsl(var(--border)) 25%,
      hsl(var(--border)) 75%,
      transparent 100%);
}

.navbar-actions:hover .separator-line {
  background: linear-gradient(to bottom,
      transparent 0%,
      hsl(var(--primary) / 0.3) 25%,
      hsl(var(--primary) / 0.3) 75%,
      transparent 100%);
}

.shadow-light {
  box-shadow: inset 0 0 5px rgba(0, 0, 0, 0.085);
}

.shadow-dark {
  box-shadow: inset 0 0 5px rgba(255, 255, 255, 0.141);
}

/* 主题相关的Logo颜色切换已移除 - 保持原色 */

/* 动态渐变背景动画 */
@keyframes gradientShift {
  0% {
    background: linear-gradient(45deg,
        hsl(var(--primary) / 0.12) 0%,
        hsl(var(--primary) / 0.08) 25%,
        hsl(var(--primary) / 0.05) 50%,
        hsl(var(--primary) / 0.08) 75%,
        hsl(var(--primary) / 0.10) 100%);
  }

  25% {
    background: linear-gradient(135deg,
        hsl(var(--primary) / 0.15) 0%,
        hsl(var(--primary) / 0.06) 25%,
        hsl(var(--primary) / 0.09) 50%,
        hsl(var(--primary) / 0.04) 75%,
        hsl(var(--primary) / 0.12) 100%);
  }

  50% {
    background: linear-gradient(225deg,
        hsl(var(--primary) / 0.10) 0%,
        hsl(var(--primary) / 0.11) 25%,
        hsl(var(--primary) / 0.06) 50%,
        hsl(var(--primary) / 0.09) 75%,
        hsl(var(--primary) / 0.07) 100%);
  }

  75% {
    background: linear-gradient(315deg,
        hsl(var(--primary) / 0.08) 0%,
        hsl(var(--primary) / 0.13) 25%,
        hsl(var(--primary) / 0.05) 50%,
        hsl(var(--primary) / 0.10) 75%,
        hsl(var(--primary) / 0.09) 100%);
  }

  100% {
    background: linear-gradient(45deg,
        hsl(var(--primary) / 0.12) 0%,
        hsl(var(--primary) / 0.08) 25%,
        hsl(var(--primary) / 0.05) 50%,
        hsl(var(--primary) / 0.08) 75%,
        hsl(var(--primary) / 0.10) 100%);
  }
}

@keyframes floatingBubble1 {

  0%,
  100% {
    transform: translateY(0px) translateX(0px) scale(1);
    opacity: 0.6;
  }

  33% {
    transform: translateY(-8px) translateX(4px) scale(1.1);
    opacity: 0.8;
  }

  66% {
    transform: translateY(4px) translateX(-6px) scale(0.9);
    opacity: 0.5;
  }
}

@keyframes floatingBubble2 {

  0%,
  100% {
    transform: translateY(0px) translateX(0px) scale(1) rotate(0deg);
    opacity: 0.5;
  }

  25% {
    transform: translateY(6px) translateX(-8px) scale(1.15) rotate(90deg);
    opacity: 0.7;
  }

  50% {
    transform: translateY(-4px) translateX(5px) scale(0.85) rotate(180deg);
    opacity: 0.6;
  }

  75% {
    transform: translateY(2px) translateX(3px) scale(1.05) rotate(270deg);
    opacity: 0.4;
  }
}

@keyframes floatingBubble3 {

  0%,
  100% {
    transform: translateY(0px) scale(1);
    opacity: 0.4;
  }

  50% {
    transform: translateY(-12px) scale(1.2);
    opacity: 0.7;
  }
}

@keyframes flowingLight {
  0% {
    background: linear-gradient(90deg,
        transparent 0%,
        transparent 40%,
        hsl(var(--primary) / 0.15) 50%,
        transparent 60%,
        transparent 100%);
    transform: translateX(-100%);
  }

  50% {
    background: linear-gradient(90deg,
        transparent 0%,
        transparent 30%,
        hsl(var(--primary) / 0.25) 50%,
        transparent 70%,
        transparent 100%);
    transform: translateX(0%);
  }

  100% {
    background: linear-gradient(90deg,
        transparent 0%,
        transparent 40%,
        hsl(var(--primary) / 0.15) 50%,
        transparent 60%,
        transparent 100%);
    transform: translateX(100%);
  }
}

/* 应用动画类 */
.animated-gradient {
  animation: gradientShift 8s ease-in-out infinite;
  border-radius: 0.75rem;
}

.floating-bubble-1 {
  animation: floatingBubble1 6s ease-in-out infinite;
}

.floating-bubble-2 {
  animation: floatingBubble2 8s ease-in-out infinite;
  animation-delay: -2s;
}

.floating-bubble-3 {
  animation: floatingBubble3 4s ease-in-out infinite;
  animation-delay: -1s;
}

.flowing-light {
  animation: flowingLight 4s ease-in-out infinite;
  animation-delay: 1s;
  border-radius: 0.75rem;
  pointer-events: none;
}

/* 悬停增强效果 */
.dynamic-gradient-bg:hover .animated-gradient {
  animation-duration: 4s;
}

.dynamic-gradient-bg:hover .floating-bubble-1 {
  animation-duration: 3s;
}

.dynamic-gradient-bg:hover .floating-bubble-2 {
  animation-duration: 4s;
}

.dynamic-gradient-bg:hover .floating-bubble-3 {
  animation-duration: 2s;
}
</style>
